import * as pdfjsLib from 'pdfjs-dist';

// Configure worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'js/latex-editor/pdf.worker.min.mjs';

export interface PdfViewerOptions {
  onPageChange?: (currentPage: number, totalPages: number) => void;
  onError?: (error: string) => void;
  onSyncTexClick?: (page: number, x: number, y: number) => void;
}

export interface PdfViewerInstance {
  container: HTMLElement;
  loadPdf: (data: Uint8Array | ArrayBuffer) => Promise<void>;
  loadPdfUrl: (url: string) => Promise<void>;
  setPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  setZoom: (zoom: number) => void;
  fitWidth: () => void;
  fitPage: () => void;
  getCurrentPage: () => number;
  getTotalPages: () => number;
  getZoom: () => number;
  highlightPosition: (page: number, y: number, height: number) => void;
  destroy: () => void;
}

export function createPdfViewer(
  container: HTMLElement,
  options: PdfViewerOptions = {}
): PdfViewerInstance {
  let pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;
  let currentPage = 1;
  let totalPages = 0;
  let currentZoom = 1.0;
  let rendering = false;

  // Style the container — light theme, horizontal flex for thumbnails + main view
  container.style.overflow = 'hidden';
  container.style.backgroundColor = '#F1F5F9'; // slate-100
  container.style.display = 'flex';
  container.style.flexDirection = 'row';
  container.style.position = 'relative';
  container.style.height = '100%';

  // Thumbnail sidebar
  const thumbSidebar = document.createElement('div');
  thumbSidebar.className = 'pdf-thumb-sidebar';
  thumbSidebar.style.width = '80px';
  thumbSidebar.style.minWidth = '80px';
  thumbSidebar.style.overflowY = 'auto';
  thumbSidebar.style.overflowX = 'hidden';
  thumbSidebar.style.backgroundColor = '#F8FAFC'; // slate-50
  thumbSidebar.style.borderRight = '1px solid #E2E8F0';
  thumbSidebar.style.padding = '4px';
  thumbSidebar.style.display = 'flex';
  thumbSidebar.style.flexDirection = 'column';
  thumbSidebar.style.alignItems = 'center';
  thumbSidebar.style.gap = '4px';
  container.appendChild(thumbSidebar);

  // Main viewing area
  const mainArea = document.createElement('div');
  mainArea.style.flex = '1';
  mainArea.style.overflow = 'auto';
  mainArea.style.display = 'flex';
  mainArea.style.flexDirection = 'column';
  mainArea.style.alignItems = 'center';
  mainArea.style.padding = '16px';
  mainArea.style.gap = '16px';
  container.appendChild(mainArea);

  const canvasContainer = document.createElement('div');
  canvasContainer.style.display = 'flex';
  canvasContainer.style.flexDirection = 'column';
  canvasContainer.style.alignItems = 'center';
  canvasContainer.style.gap = '8px';
  mainArea.appendChild(canvasContainer);

  // Track page wrappers for SyncTeX highlighting
  const pageWrappers: HTMLDivElement[] = [];

  // Observe scroll to track current page and highlight thumbnail
  function updateCurrentPageFromScroll(): void {
    const wrappers = canvasContainer.querySelectorAll('.pdf-page-wrapper');
    const mainRect = mainArea.getBoundingClientRect();
    const mainMid = mainRect.top + mainRect.height / 2;

    for (let i = 0; i < wrappers.length; i++) {
      const rect = wrappers[i].getBoundingClientRect();
      if (rect.top <= mainMid && rect.bottom >= mainMid) {
        if (currentPage !== i + 1) {
          currentPage = i + 1;
          updateThumbnailHighlight();
          if (options.onPageChange) {
            options.onPageChange(currentPage, totalPages);
          }
        }
        break;
      }
    }
  }

  function updateThumbnailHighlight(): void {
    const thumbs = thumbSidebar.querySelectorAll('.pdf-thumb');
    thumbs.forEach((thumb, i) => {
      const el = thumb as HTMLElement;
      if (i + 1 === currentPage) {
        el.style.outline = '2px solid #2563EB';
        el.style.outlineOffset = '1px';
      } else {
        el.style.outline = 'none';
      }
    });
  }

  mainArea.addEventListener('scroll', () => {
    requestAnimationFrame(updateCurrentPageFromScroll);
  });

  async function renderAllPages(): Promise<void> {
    if (!pdfDoc) return;

    canvasContainer.innerHTML = '';
    pageWrappers.length = 0;

    for (let i = 1; i <= totalPages; i++) {
      try {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: currentZoom * window.devicePixelRatio });
        const displayViewport = page.getViewport({ scale: currentZoom });

        // Page wrapper (positions canvas + text layer + highlight layer)
        const wrapper = document.createElement('div');
        wrapper.className = 'pdf-page-wrapper';
        wrapper.style.position = 'relative';
        wrapper.style.width = `${displayViewport.width}px`;
        wrapper.style.height = `${displayViewport.height}px`;
        wrapper.style.marginBottom = '8px';
        wrapper.dataset.page = String(i);
        pageWrappers.push(wrapper);

        // Canvas for rendering
        const canvas = document.createElement('canvas');
        canvas.style.borderRadius = '4px';
        canvas.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        canvas.style.display = 'block';
        canvas.style.width = `${displayViewport.width}px`;
        canvas.style.height = `${displayViewport.height}px`;
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        wrapper.appendChild(canvas);

        // Text layer for selection and click targets
        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'pdf-text-layer';
        textLayerDiv.style.position = 'absolute';
        textLayerDiv.style.top = '0';
        textLayerDiv.style.left = '0';
        textLayerDiv.style.width = `${displayViewport.width}px`;
        textLayerDiv.style.height = `${displayViewport.height}px`;
        textLayerDiv.style.overflow = 'hidden';
        textLayerDiv.style.opacity = '0.25';
        textLayerDiv.style.lineHeight = '1.0';
        wrapper.appendChild(textLayerDiv);

        // Highlight overlay for SyncTeX
        const highlightLayer = document.createElement('div');
        highlightLayer.className = 'pdf-highlight-layer';
        highlightLayer.style.position = 'absolute';
        highlightLayer.style.top = '0';
        highlightLayer.style.left = '0';
        highlightLayer.style.width = '100%';
        highlightLayer.style.height = '100%';
        highlightLayer.style.pointerEvents = 'none';
        wrapper.appendChild(highlightLayer);

        canvasContainer.appendChild(wrapper);

        // Render canvas
        const ctx = canvas.getContext('2d')!;
        await page.render({
          canvasContext: ctx,
          viewport: viewport,
        }).promise;

        // Render text layer
        try {
          const textContent = await page.getTextContent();
          const textItems = textContent.items as any[];
          for (const item of textItems) {
            if (!item.str) continue;
            const tx = pdfjsLib.Util.transform(
              displayViewport.transform,
              item.transform
            );
            const span = document.createElement('span');
            span.textContent = item.str;
            span.style.position = 'absolute';
            span.style.left = `${tx[4]}px`;
            span.style.top = `${displayViewport.height - tx[5]}px`;
            span.style.fontSize = `${Math.abs(tx[3])}px`;
            span.style.fontFamily = item.fontName || 'serif';
            span.style.transformOrigin = '0% 0%';
            span.style.whiteSpace = 'pre';
            span.style.color = 'transparent';
            span.style.cursor = 'text';
            textLayerDiv.appendChild(span);
          }
        } catch (e) {
          // Text layer is optional — don't break rendering
          console.warn(`[PdfViewer] Text layer error on page ${i}:`, e);
        }

        // Click handler for inverse SyncTeX
        const pageNum = i;
        wrapper.addEventListener('click', (e: MouseEvent) => {
          if (!e.ctrlKey && !e.metaKey) return; // Only on Ctrl+click
          const rect = wrapper.getBoundingClientRect();
          const clickX = (e.clientX - rect.left) / currentZoom;
          const clickY = (e.clientY - rect.top) / currentZoom;
          // Convert to PDF points (72 DPI, origin at bottom-left)
          const pdfX = clickX * 72 / 96;
          const pdfY = (displayViewport.height / currentZoom - clickY) * 72 / 96;
          if (options.onSyncTexClick) {
            options.onSyncTexClick(pageNum, pdfX, pdfY);
          }
        });

      } catch (e) {
        console.error(`[PdfViewer] Error rendering page ${i}:`, e);
      }
    }

    if (options.onPageChange) {
      options.onPageChange(currentPage, totalPages);
    }

    // Render thumbnails
    await renderThumbnails();
  }

  async function renderThumbnails(): Promise<void> {
    if (!pdfDoc) return;
    thumbSidebar.innerHTML = '';

    const thumbWidth = 64; // px
    for (let i = 1; i <= totalPages; i++) {
      try {
        const page = await pdfDoc.getPage(i);
        const unscaledViewport = page.getViewport({ scale: 1.0 });
        const thumbScale = thumbWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale: thumbScale * window.devicePixelRatio });
        const displayViewport = page.getViewport({ scale: thumbScale });

        const thumbWrapper = document.createElement('div');
        thumbWrapper.className = 'pdf-thumb';
        thumbWrapper.style.cursor = 'pointer';
        thumbWrapper.style.borderRadius = '2px';
        thumbWrapper.style.overflow = 'hidden';
        thumbWrapper.style.position = 'relative';
        if (i === currentPage) {
          thumbWrapper.style.outline = '2px solid #2563EB';
          thumbWrapper.style.outlineOffset = '1px';
        }

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${displayViewport.width}px`;
        canvas.style.height = `${displayViewport.height}px`;
        canvas.style.display = 'block';
        canvas.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        thumbWrapper.appendChild(canvas);

        // Page number label
        const label = document.createElement('div');
        label.textContent = String(i);
        label.style.fontSize = '9px';
        label.style.textAlign = 'center';
        label.style.color = '#64748B';
        label.style.marginTop = '1px';
        thumbWrapper.appendChild(label);

        // Click → scroll to page
        const pageNum = i;
        thumbWrapper.addEventListener('click', () => {
          if (pageWrappers[pageNum - 1]) {
            pageWrappers[pageNum - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
            currentPage = pageNum;
            updateThumbnailHighlight();
            if (options.onPageChange) {
              options.onPageChange(currentPage, totalPages);
            }
          }
        });

        thumbSidebar.appendChild(thumbWrapper);

        // Render thumbnail canvas
        const ctx = canvas.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch (e) {
        console.warn(`[PdfViewer] Thumbnail error page ${i}:`, e);
      }
    }
  }

  const instance: PdfViewerInstance = {
    container,

    async loadPdf(data: Uint8Array | ArrayBuffer) {
      try {
        if (pdfDoc) {
          await pdfDoc.destroy();
        }
        pdfDoc = await pdfjsLib.getDocument({ data }).promise;
        totalPages = pdfDoc.numPages;
        currentPage = 1;
        await renderAllPages();
      } catch (e: any) {
        console.error('[PdfViewer] Load error:', e);
        if (options.onError) {
          options.onError(`Failed to load PDF: ${e.message}`);
        }
      }
    },

    async loadPdfUrl(url: string) {
      try {
        if (pdfDoc) {
          await pdfDoc.destroy();
        }
        pdfDoc = await pdfjsLib.getDocument(url).promise;
        totalPages = pdfDoc.numPages;
        currentPage = 1;
        await renderAllPages();
      } catch (e: any) {
        console.error('[PdfViewer] Load error:', e);
        if (options.onError) {
          options.onError(`Failed to load PDF: ${e.message}`);
        }
      }
    },

    setPage(page: number) {
      if (page >= 1 && page <= totalPages) {
        currentPage = page;
        if (pageWrappers[page - 1]) {
          pageWrappers[page - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (options.onPageChange) {
          options.onPageChange(currentPage, totalPages);
        }
      }
    },

    nextPage() {
      instance.setPage(currentPage + 1);
    },

    prevPage() {
      instance.setPage(currentPage - 1);
    },

    setZoom(zoom: number) {
      currentZoom = Math.max(0.25, Math.min(5.0, zoom));
      renderAllPages();
    },

    fitWidth() {
      if (pdfDoc) {
        pdfDoc.getPage(1).then((page) => {
          const viewport = page.getViewport({ scale: 1.0 });
          const areaWidth = mainArea.clientWidth - 48;
          currentZoom = areaWidth / viewport.width;
          renderAllPages();
        });
      }
    },

    fitPage() {
      if (pdfDoc) {
        pdfDoc.getPage(1).then((page) => {
          const viewport = page.getViewport({ scale: 1.0 });
          const areaWidth = mainArea.clientWidth - 48;
          const areaHeight = mainArea.clientHeight - 48;
          const scaleX = areaWidth / viewport.width;
          const scaleY = areaHeight / viewport.height;
          currentZoom = Math.min(scaleX, scaleY);
          renderAllPages();
        });
      }
    },

    getCurrentPage() { return currentPage; },
    getTotalPages() { return totalPages; },
    getZoom() { return currentZoom; },

    highlightPosition(page: number, y: number, height: number) {
      // Clear existing highlights
      canvasContainer.querySelectorAll('.synctex-highlight').forEach(el => el.remove());

      const wrapper = pageWrappers[page - 1];
      if (!wrapper) return;

      // Scroll to page
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Draw highlight rectangle
      const highlightLayer = wrapper.querySelector('.pdf-highlight-layer');
      if (!highlightLayer) return;

      const displayHeight = parseFloat(wrapper.style.height);
      // Convert from PDF points (bottom-left origin) to CSS pixels (top-left origin)
      const cssY = displayHeight - (y * 96 / 72 * currentZoom);
      const cssH = Math.max(height * 96 / 72 * currentZoom, 20);

      const highlight = document.createElement('div');
      highlight.className = 'synctex-highlight';
      highlight.style.position = 'absolute';
      highlight.style.left = '0';
      highlight.style.width = '100%';
      highlight.style.top = `${cssY - cssH / 2}px`;
      highlight.style.height = `${cssH}px`;
      highlight.style.backgroundColor = 'rgba(37, 99, 235, 0.15)'; // blue-600 with alpha
      highlight.style.borderLeft = '3px solid #2563EB';
      highlight.style.transition = 'opacity 0.5s';
      highlight.style.pointerEvents = 'none';
      highlightLayer.appendChild(highlight);

      // Fade out after 2s
      setTimeout(() => {
        highlight.style.opacity = '0';
        setTimeout(() => highlight.remove(), 500);
      }, 2000);
    },

    destroy() {
      if (pdfDoc) {
        pdfDoc.destroy();
        pdfDoc = null;
      }
      canvasContainer.innerHTML = '';
      thumbSidebar.innerHTML = '';
      pageWrappers.length = 0;
    },
  };

  return instance;
}
